import {
  ArrowRight,
  Play,
  Save,
  GitBranch,
  Bot,
  CheckCircle,
  Zap,
  PlusCircle,
  Send,
} from "lucide-react"
import type { WorkflowMockupConfig, WorkflowNode } from "../types"

interface WorkflowBuilderMockupProps {
  /** Configuration for the workflow mockup */
  config: WorkflowMockupConfig
}

/**
 * Returns the appropriate icon component for a workflow node type.
 */
function getNodeIcon(type: WorkflowNode["type"]) {
  switch (type) {
    case "trigger":
      return Zap
    case "agent":
      return Bot
    case "conditional":
      return GitBranch
    case "action":
      return CheckCircle
    case "output":
      return Send
    default:
      return CheckCircle
  }
}

/**
 * Returns the color classes for a workflow node type.
 */
function getNodeColors(type: WorkflowNode["type"], color?: WorkflowNode["color"]) {
  if (color) {
    const colorMap: Record<NonNullable<WorkflowNode["color"]>, string> = {
      primary: "border-primary/30 bg-primary/20 text-primary",
      secondary: "border-secondary/30 bg-secondary/20 text-secondary",
      blue: "border-blue-500/30 bg-blue-500/20 text-blue-400",
      purple: "border-purple-500/30 bg-purple-500/20 text-purple-400",
      yellow: "border-yellow-500/30 bg-yellow-500/20 text-yellow-400",
      green: "border-green-500/30 bg-green-500/20 text-green-400",
    }
    return colorMap[color]
  }

  switch (type) {
    case "trigger":
      return "border-yellow-500/30 bg-yellow-500/20 text-yellow-400"
    case "agent":
      return "border-purple-500/30 bg-purple-500/20 text-purple-400"
    case "conditional":
      return "border-blue-500/30 bg-blue-500/20 text-blue-400"
    case "action":
      return "border-green-500/30 bg-green-500/20 text-green-400"
    case "output":
      return "border-primary/30 bg-primary/20 text-primary"
    default:
      return "border-neutral-500/30 bg-neutral-500/20 text-neutral-400"
  }
}

/**
 * Workflow Builder Mockup component that visually represents the workflow builder interface.
 * This is a non-functional visual representation used for marketing and demo purposes.
 * Accepts configuration props to customize the display for different use cases.
 */
export function WorkflowBuilderMockup({ config }: WorkflowBuilderMockupProps) {
  const nodePositions = [
    { top: 8, left: 8 },
    { top: 80, left: 160 },
    { top: 152, left: 312 },
    { top: 224, left: 464 },
    { top: 80, left: 464 },
  ]

  return (
    <div className="w-full rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="h-6 w-6 rounded bg-neutral-800 flex items-center justify-center">
            <ArrowRight className="h-3 w-3 rotate-180 text-neutral-500" />
          </div>
          <span className="text-sm font-medium text-white">{config.workflowName}</span>
          <span
            className={`rounded px-2 py-0.5 text-xs ${
              config.status === "Active"
                ? "bg-primary/20 text-primary"
                : "bg-neutral-700 text-neutral-400"
            }`}
          >
            {config.status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded bg-neutral-800 px-2.5 py-1 text-xs text-neutral-400 flex items-center gap-1">
            <Play className="h-3 w-3" />
            Run
          </div>
          <div className="rounded bg-primary px-2.5 py-1 text-xs text-white flex items-center gap-1">
            <Save className="h-3 w-3" />
            Save
          </div>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="flex min-h-[320px]">
        {/* Left: Mini Node Palette */}
        <div className="w-48 border-r border-neutral-800 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-3">
            Nodes
          </div>
          <div className="space-y-2">
            {[
              {
                icon: <Zap className="h-3 w-3" />,
                label: "Trigger",
                color: "bg-yellow-500/20 text-yellow-400",
              },
              {
                icon: <Bot className="h-3 w-3" />,
                label: "AI Agent",
                color: "bg-purple-500/20 text-purple-400",
              },
              {
                icon: <GitBranch className="h-3 w-3" />,
                label: "Conditional",
                color: "bg-blue-500/20 text-blue-400",
              },
              {
                icon: <CheckCircle className="h-3 w-3" />,
                label: "Action",
                color: "bg-green-500/20 text-green-400",
              },
            ].map((node, i) => (
              <div
                key={i}
                className="flex items-center gap-2 rounded border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 cursor-grab"
              >
                <div className={`rounded p-1 ${node.color}`}>{node.icon}</div>
                <span className="text-xs text-neutral-300">{node.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Canvas with Nodes */}
        <div className="flex-1 relative p-6 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:20px_20px]">
          {/* Render workflow nodes */}
          {config.nodes.slice(0, 5).map((node, index) => {
            const Icon = getNodeIcon(node.type)
            const colors = getNodeColors(node.type, node.color)
            const position = nodePositions[index] || { top: 8 + index * 72, left: 8 + index * 152 }

            return (
              <div
                key={node.id}
                className="absolute"
                style={{ top: position.top, left: position.left }}
              >
                <div
                  className={`flex items-center gap-2 rounded-lg border bg-neutral-800 px-3 py-2 shadow-lg ${colors.split(" ")[0]}`}
                >
                  <div className={`rounded p-1 ${colors}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-xs font-medium text-white">{node.label}</div>
                    <div className="text-xs text-neutral-500">{node.sublabel}</div>
                  </div>
                </div>
              </div>
            )
          })}

          {/* Connection lines - simplified SVG connections */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{ overflow: "visible" }}
          >
            {/* Line from node 1 to node 2 */}
            <path
              d="M130,35 C180,35 140,107 200,107"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
            {/* Line from node 2 to node 3 */}
            <path
              d="M330,107 C380,107 340,179 400,179"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
            {/* Branch line from node 3 to node 4 (down) */}
            <path
              d="M450,200 C450,230 500,230 500,251"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
            {/* Branch line from node 3 to node 5 (right/up) */}
            <path
              d="M520,179 C550,179 520,107 560,107"
              fill="none"
              stroke="#4B5563"
              strokeWidth="2"
            />
          </svg>

          {/* Add Node Button */}
          <div className="absolute bottom-8 right-8">
            <div className="flex items-center justify-center h-8 w-8 rounded-full border-2 border-dashed border-neutral-600 bg-neutral-800/50 cursor-pointer hover:border-primary/50 transition-colors">
              <PlusCircle className="h-4 w-4 text-neutral-500" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
